import { useState, useEffect, useCallback } from 'react';

// Hook para manejar el recordatorio de importación diaria de CSV SENEBI
const useImportSenebiReminder = () => {
  const [showReminder, setShowReminder] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [reminderShownToday, setReminderShownToday] = useState(false);
  const [snoozeTimeout, setSnoozeTimeout] = useState(null);

  // Verificar si ya se mostró el recordatorio hoy
  useEffect(() => {
    const today = new Date().toDateString();
    const lastReminderDate = localStorage.getItem('lastSenebiImportReminderDate');
    
    if (lastReminderDate === today) {
      setReminderShownToday(true);
    }

    // Limpiar timeout al desmontar componente
    return () => {
      if (snoozeTimeout) {
        clearTimeout(snoozeTimeout);
      }
    };
  }, [snoozeTimeout]);

  // Función para mostrar recordatorio programado
  const showScheduledReminder = useCallback(() => {
    setShowReminder(true);
    setCurrentTime(new Date());
  }, []);

  // Función para verificar si es hora de mostrar el recordatorio
  const checkReminderTime = useCallback(() => {
    const now = new Date();
    const hour = now.getHours();
    const minutes = now.getMinutes();
    
    // Mostrar recordatorio a las 17:30 (solo una vez por día)
    const isReminderTime = hour === 17 && minutes === 30;
    
    // Solo en días hábiles (lunes a viernes)
    const dayOfWeek = now.getDay(); // 0 = domingo, 6 = sábado
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    
    if (isReminderTime && isWeekday && !reminderShownToday) {
      showScheduledReminder();
      
      // Marcar que ya se mostró hoy (pero permitir snooze)
      const today = now.toDateString();
      localStorage.setItem('lastSenebiImportReminderDate', today);
      setReminderShownToday(true);
    }
  }, [reminderShownToday, showScheduledReminder]);

  // Verificar cada minuto si es hora del recordatorio
  useEffect(() => {
    // Verificar inmediatamente al cargar
    checkReminderTime();
    
    // Luego verificar cada minuto
    const interval = setInterval(checkReminderTime, 60000); // 60 segundos
    
    return () => clearInterval(interval);
  }, [checkReminderTime]);

  // Función para cerrar el recordatorio y programar uno en 5 minutos
  const dismissReminder = useCallback(() => {
    setShowReminder(false);
    
    // Programar recordatorio en 5 minutos
    const timeout = setTimeout(() => {
      console.log('🔔 Recordatorio SENEBI - Programado por snooze de 5 minutos');
      showScheduledReminder();
    }, 5 * 60 * 1000); // 5 minutos en millisegundos
    
    setSnoozeTimeout(timeout);
    console.log('⏰ Recordatorio SENEBI pospuesto por 5 minutos');
  }, [showScheduledReminder]);

  // Función para cerrar y no mostrar más (si usuario hace import)
  const handleImportAction = useCallback(() => {
    setShowReminder(false);
    
    // Limpiar timeout de snooze si existe
    if (snoozeTimeout) {
      clearTimeout(snoozeTimeout);
      setSnoozeTimeout(null);
    }
    
    console.log('✅ Usuario fue a importar CSV - Recordatorio completado');
  }, [snoozeTimeout]);

  // Función para forzar mostrar el recordatorio (para testing)
  const forceShowReminder = useCallback(() => {
    showScheduledReminder();
  }, [showScheduledReminder]);

  // Exponer función de testing globalmente
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugSenebiReminder = {
        forceShow: forceShowReminder,
        currentState: {
          showReminder,
          reminderShownToday,
          snoozeActive: !!snoozeTimeout
        }
      };
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete window.debugSenebiReminder;
      }
    };
  }, [forceShowReminder, showReminder, reminderShownToday, snoozeTimeout]);

  return {
    showReminder,
    currentTime,
    dismissReminder,
    handleImportAction,
    forceShowReminder
  };
};

export default useImportSenebiReminder;